import React from 'react';
import { StatusBadge } from './PocUi';
import { DECISION_BASIS, OUTCOME_LABELS, formatTimestamp, humanize, inputLabel, reasonText } from './pocFormat';

// Explains a decision the backend stored. Everything shown comes from the API
// response; codes without a known description are shown as received, never
// hidden and never read as a pass.

const THRESHOLD_NOTE = 'An eligible claim above the configured authorization threshold is referred for manual review. It is not automatically rejected.';

function Term({ label, children }) {
    return (
        <div className="min-w-0">
            <dt className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">{label}</dt>
            <dd className="text-sm text-text-main mt-1 break-words">{children ?? '—'}</dd>
        </div>
    );
}

const outcome = (code) => (code ? <>{OUTCOME_LABELS[code] || humanize(code)} <code className="text-[11px] text-text-muted">({code})</code></> : '—');

export function ReasonCodeList({ codes }) {
    if (!codes?.length) return <p className="text-sm text-text-muted">No reason codes were recorded.</p>;
    return (
        <ul className="space-y-2" aria-label="Reason codes">
            {codes.map((code, i) => {
                const text = reasonText(code);
                return (
                    <li key={`${code}-${i}`} className="text-sm flex flex-col sm:flex-row sm:gap-3">
                        <code className="font-semibold text-[12px] shrink-0 sm:w-64">{code}</code>
                        <span className={text ? '' : 'text-text-muted italic'}>
                            {text || 'Unrecognised reason code. No description is available; see the technical details.'}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

export function MissingInputs({ inputs }) {
    if (!inputs?.length) return null;
    return (
        <div className="border border-[#B7791F] bg-[#FFF8E8] p-4 space-y-2" role="note" aria-label="Missing information">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#6B4200]">Missing information</p>
            <ul className="list-disc pl-5 text-sm">
                {inputs.map((name) => <li key={name}>{inputLabel(name)} <code className="text-[11px] text-text-muted">({name})</code></li>)}
            </ul>
            <p className="text-xs text-text-muted">Missing information is referred for manual review. It is never treated as satisfying a rule.</p>
        </div>
    );
}

/** The current (or any single) decision event. */
export function DecisionSummary({ decision, isAdmin }) {
    if (!decision) {
        return <p className="text-sm text-text-muted">No decision has been recorded for this claim yet.</p>;
    }
    const isError = decision.final_decision === 'EVALUATION_ERROR';
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-4">
                <StatusBadge status={decision.final_decision} size="lg" />
                <p className="text-sm text-text-main">{DECISION_BASIS[decision.decision_basis] || humanize(decision.decision_basis)}</p>
            </div>
            {isError && (
                <p role="note" className="text-sm border border-[#2B2B2B] p-3">
                    The evaluation failed for a technical reason. This is not a rejected warranty claim; an administrator can re-evaluate it.
                </p>
            )}
            {decision.authorization_outcome === 'ABOVE_THRESHOLD' && <p role="note" className="text-sm border-l-2 border-[#B7791F] pl-3">{THRESHOLD_NOTE}</p>}
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                <Term label="Warranty eligibility">{outcome(decision.warranty_outcome)}</Term>
                <Term label="Authorization">{outcome(decision.authorization_outcome)}</Term>
                <Term label="Decision basis"><code className="text-[12px]">{decision.decision_basis}</code></Term>
                <Term label="Policy">{decision.policy_id || 'None selected'}</Term>
                <Term label="Authorization rule">{decision.authorization_rule_id || 'None selected'}</Term>
                <Term label="Evaluation version">{decision.evaluation_version}</Term>
                <Term label="Evaluated at">{formatTimestamp(decision.evaluated_at)}</Term>
                {isAdmin && <Term label="Evaluated by">{decision.evaluated_by || '—'}</Term>}
            </dl>
            <MissingInputs inputs={decision.missing_inputs} />
            <div>
                <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-2">Reason codes</h4>
                <ReasonCodeList codes={decision.reason_codes} />
            </div>
            {decision.reasons?.length > 0 && (
                <div>
                    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-2">Evaluator explanation</h4>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                        {decision.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

const RULE_STATUS = { PASS: 'Pass', FAIL: 'Fail', UNKNOWN: 'Unknown — not a pass', NOT_APPLICABLE: 'Not applicable' };

/** Technical details from a stored evaluation snapshot (history events only). */
export function TechnicalDetails({ snapshot, isAdmin }) {
    const evaluation = snapshot?.evaluation;
    const warranty = evaluation?.warranty_policy;
    const auth = evaluation?.authorization_threshold;
    return (
        <details className="border border-line">
            <summary className="cursor-pointer px-4 py-3 text-[11px] font-semibold uppercase tracking-widest focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                Technical Evaluation Details
            </summary>
            <div className="p-4 space-y-5 text-sm">
                {!evaluation && <p className="text-text-muted">No evaluation snapshot is available for this event.</p>}
                {warranty && (
                    <div className="space-y-3">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">Warranty rule results</p>
                        {warranty.evaluated_rules?.length ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-text-muted uppercase tracking-widest">
                                            <th className="py-2 pr-4 font-semibold">Rule</th>
                                            <th className="py-2 pr-4 font-semibold">Result</th>
                                            <th className="py-2 pr-4 font-semibold">Reason code</th>
                                            <th className="py-2 font-semibold">Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {warranty.evaluated_rules.map((rule) => (
                                            <tr key={rule.rule_id} className="border-t border-line align-top">
                                                <td className="py-2 pr-4">{humanize(rule.rule_type)}</td>
                                                <td className="py-2 pr-4 font-semibold">{RULE_STATUS[rule.status] || rule.status}</td>
                                                <td className="py-2 pr-4"><code>{rule.reason_code}</code></td>
                                                <td className="py-2">{rule.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : <p className="text-text-muted">No warranty rules ran ({warranty.reason_code || 'no reason code'}).</p>}
                        {warranty.selection && (
                            <p className="text-xs text-text-muted">
                                Candidate policies: {warranty.selection.candidate_ids?.join(', ') || 'none'} · Selected: {warranty.selection.selected_id || 'none'}
                                {warranty.selection.conflicting_ids?.length > 0 && <> · Conflicting: {warranty.selection.conflicting_ids.join(', ')}</>}
                            </p>
                        )}
                        {warranty.semantics_version && <p className="text-xs text-text-muted">Semantics: {warranty.semantics_version}</p>}
                    </div>
                )}
                {auth && (
                    <p className="text-xs">
                        Authorization: {OUTCOME_LABELS[auth.outcome] || auth.outcome}
                        {auth.reason_code && <> (<code>{auth.reason_code}</code>)</>}
                        {isAdmin && auth.threshold_amount && <> · Threshold {auth.currency} {auth.threshold_amount}</>}
                    </p>
                )}
                {snapshot?.facts && (
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-2">Facts evaluated</p>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                            {Object.entries(snapshot.facts).map(([k, v]) => (
                                <div key={k} className="flex gap-2">
                                    <dt className="text-text-muted">{inputLabel(k)}:</dt>
                                    <dd>{v === null || v === undefined ? 'not provided' : Array.isArray(v) ? (v.join(', ') || 'none') : String(v)}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                )}
                {isAdmin && snapshot && (
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-2">Stored snapshot (administrators)</p>
                        <pre className="text-[11px] bg-background border border-line p-3 overflow-x-auto max-h-80">{JSON.stringify(snapshot, null, 2)}</pre>
                    </div>
                )}
            </div>
        </details>
    );
}

/** Append-only decision history, oldest first. */
export function HistoryTimeline({ events, isAdmin }) {
    if (!events?.length) return <p className="text-sm text-text-muted">No decision events have been recorded.</p>;
    const ordered = [...events].sort((a, b) => a.sequence_no - b.sequence_no);
    return (
        <ol className="space-y-6" aria-label="Decision history">
            {ordered.map((event) => (
                <li key={event.decision_id} className="border-l-2 border-primary pl-5 space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs font-semibold uppercase tracking-widest">Decision #{event.sequence_no}</span>
                        <span className="text-[10px] uppercase tracking-widest border border-line px-2 py-0.5">{event.event_type}</span>
                        <span className="text-xs text-text-muted">{formatTimestamp(event.evaluated_at)}</span>
                    </div>
                    <DecisionSummary decision={event} isAdmin={isAdmin} />
                    <TechnicalDetails snapshot={event.evaluation} isAdmin={isAdmin} />
                </li>
            ))}
        </ol>
    );
}
